import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MainLayout } from "../components/layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import attendanceService from "../services/attendance.service";

function statusLabel(status: string | null | undefined): string {
  switch (status) {
    case "checked_in":
      return "เข้างานแล้ว";
    case "completed":
      return "ออกงานแล้ว";
    case "missing_checkout":
      return "ขาดการออกงาน";
    default:
      return "ยังไม่ได้เข้างาน";
  }
}

function statusClass(status: string | null | undefined): string {
  switch (status) {
    case "checked_in":
      return "bg-blue-100 text-blue-800";
    case "completed":
      return "bg-green-100 text-green-800";
    case "missing_checkout":
      return "bg-amber-100 text-amber-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

function formatDateTime(value?: string | null) {
  if (!value) return "-";
  return new Date(value).toLocaleString("th-TH", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function toDateTimeLocal(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

function fromDateTimeLocal(value: string) {
  return value ? new Date(value).toISOString() : undefined;
}

async function getCurrentLocation() {
  if (!navigator.geolocation) {
    throw new Error("เบราว์เซอร์นี้ไม่รองรับการระบุตำแหน่ง");
  }

  return new Promise<{ latitude: number; longitude: number; accuracy: number }>(
    (resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) =>
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          }),
        () => reject(new Error("ไม่สามารถอ่านตำแหน่งปัจจุบันได้")),
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        }
      );
    }
  );
}

export function WfhAttendancePage() {
  const queryClient = useQueryClient();
  const [checkInNote, setCheckInNote] = useState("");
  const [checkOutNote, setCheckOutNote] = useState("");
  const [exceptionType, setExceptionType] = useState<
    "missing_checkout" | "manual_entry" | "time_correction"
  >("manual_entry");
  const [exceptionReason, setExceptionReason] = useState("");
  const [requestedCheckInAt, setRequestedCheckInAt] = useState("");
  const [requestedCheckOutAt, setRequestedCheckOutAt] = useState("");
  const [pageError, setPageError] = useState("");

  const todayQuery = useQuery({
    queryKey: ["attendance", "today"],
    queryFn: () => attendanceService.getToday(),
  });

  const historyQuery = useQuery({
    queryKey: ["attendance", "history"],
    queryFn: () => attendanceService.getHistory(1, 10),
  });

  const exceptionsQuery = useQuery({
    queryKey: ["attendance", "exceptions", "self"],
    queryFn: () => attendanceService.getExceptions("self", 1, 10),
  });

  const refreshAttendance = () => {
    queryClient.invalidateQueries({ queryKey: ["attendance"] });
  };

  const checkInMutation = useMutation({
    mutationFn: async () => {
      setPageError("");
      const location = await getCurrentLocation();
      return attendanceService.checkIn({
        note: checkInNote,
        location,
      });
    },
    onSuccess: () => {
      setCheckInNote("");
      refreshAttendance();
    },
    onError: (error: any) => {
      setPageError(
        error?.response?.data?.error?.message ||
          error?.message ||
          "ไม่สามารถบันทึกการเข้างานได้"
      );
    },
  });

  const checkOutMutation = useMutation({
    mutationFn: async () => {
      setPageError("");
      const location = await getCurrentLocation();
      return attendanceService.checkOut({
        note: checkOutNote,
        location,
      });
    },
    onSuccess: () => {
      setCheckOutNote("");
      refreshAttendance();
    },
    onError: (error: any) => {
      setPageError(
        error?.response?.data?.error?.message ||
          error?.message ||
          "ไม่สามารถบันทึกการออกงานได้"
      );
    },
  });

  const exceptionMutation = useMutation({
    mutationFn: () =>
      attendanceService.createException({
        attendanceRecordId: todayQuery.data?.record?.id || undefined,
        type: exceptionType,
        reason: exceptionReason,
        requestedCheckInAt: fromDateTimeLocal(requestedCheckInAt),
        requestedCheckOutAt: fromDateTimeLocal(requestedCheckOutAt),
      }),
    onSuccess: () => {
      setExceptionReason("");
      setRequestedCheckInAt("");
      setRequestedCheckOutAt("");
      refreshAttendance();
    },
    onError: (error: any) => {
      setPageError(
        error?.response?.data?.error?.message ||
          error?.message ||
          "ไม่สามารถส่งคำขอแก้ไขเวลาได้"
      );
    },
  });

  const isLoading =
    todayQuery.isLoading || historyQuery.isLoading || exceptionsQuery.isLoading;

  const record = todayQuery.data?.record || null;
  const defaultExceptionType = useMemo(() => {
    if (!record) return "manual_entry";
    if (record.status === "missing_checkout") return "missing_checkout";
    return "time_correction";
  }, [record]);

  React.useEffect(() => {
    setExceptionType(defaultExceptionType);
    setRequestedCheckInAt(toDateTimeLocal(record?.checkInAt));
    setRequestedCheckOutAt(toDateTimeLocal(record?.checkOutAt));
  }, [defaultExceptionType, record?.checkInAt, record?.checkOutAt]);

  if (isLoading) {
    return (
      <MainLayout title="WFH Check-in/out">
        <div className="text-center py-16 text-gray-600">กำลังโหลดข้อมูล...</div>
      </MainLayout>
    );
  }

  if (todayQuery.isError) {
    return (
      <MainLayout title="WFH Check-in/out">
        <div className="max-w-3xl mx-auto bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {(
            (todayQuery.error as any)?.response?.data?.error?.message ||
            (todayQuery.error as Error)?.message ||
            "ไม่สามารถโหลดหน้าระบบ WFH ได้"
          ).toString()}
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="WFH Check-in/out">
      <div className="max-w-6xl mx-auto space-y-6">
        {pageError && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
            {pageError}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>สถานะ WFH วันนี้</span>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${statusClass(
                    record?.status
                  )}`}
                >
                  {statusLabel(record?.status)}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="p-4 rounded-lg bg-slate-50">
                  <div className="text-gray-500 mb-1">วันที่ทำงาน</div>
                  <div className="font-semibold">{todayQuery.data?.workDate}</div>
                </div>
                <div className="p-4 rounded-lg bg-slate-50">
                  <div className="text-gray-500 mb-1">เวลามาตรฐาน</div>
                  <div className="font-semibold">
                    {todayQuery.data?.schedule.startTime || "-"} -{" "}
                    {todayQuery.data?.schedule.endTime || "-"}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {todayQuery.data?.schedule.timezone} /{" "}
                    {todayQuery.data?.schedule.source}
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-slate-50">
                  <div className="text-gray-500 mb-1">เวลาเข้างาน</div>
                  <div className="font-semibold">{formatDateTime(record?.checkInAt)}</div>
                </div>
                <div className="p-4 rounded-lg bg-slate-50">
                  <div className="text-gray-500 mb-1">เวลาออกงาน</div>
                  <div className="font-semibold">{formatDateTime(record?.checkOutAt)}</div>
                </div>
                <div className="p-4 rounded-lg bg-slate-50">
                  <div className="text-gray-500 mb-1">มาสาย</div>
                  <div className="font-semibold">{record?.lateMinutes ?? 0} นาที</div>
                </div>
                <div className="p-4 rounded-lg bg-slate-50">
                  <div className="text-gray-500 mb-1">เวลาทำงาน</div>
                  <div className="font-semibold">
                    {record?.workedMinutes ? `${record.workedMinutes} นาที` : "-"}
                  </div>
                </div>
              </div>

              {todayQuery.data?.actions.canCheckIn && (
                <div className="space-y-3 border rounded-lg p-4">
                  <div className="font-semibold">Check-in WFH</div>
                  <textarea
                    value={checkInNote}
                    onChange={(e) => setCheckInNote(e.target.value)}
                    rows={3}
                    placeholder="ระบุสถานที่ทำงานหรือแผนงานช่วงเช้า"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                  <Button
                    onClick={() => checkInMutation.mutate()}
                    disabled={!checkInNote.trim() || checkInMutation.isPending}
                  >
                    {checkInMutation.isPending
                      ? "กำลังยืนยันเข้างาน..."
                      : "ยืนยันเข้างาน WFH"}
                  </Button>
                </div>
              )}

              {todayQuery.data?.actions.canCheckOut && (
                <div className="space-y-3 border rounded-lg p-4">
                  <div className="font-semibold">Check-out WFH</div>
                  <textarea
                    value={checkOutNote}
                    onChange={(e) => setCheckOutNote(e.target.value)}
                    rows={3}
                    placeholder="สรุปงานที่ทำวันนี้หรือสถานะส่งมอบ"
                    className="w-full border rounded-lg px-3 py-2"
                  />
                  <Button
                    onClick={() => checkOutMutation.mutate()}
                    disabled={!checkOutNote.trim() || checkOutMutation.isPending}
                  >
                    {checkOutMutation.isPending
                      ? "กำลังยืนยันออกงาน..."
                      : "ยืนยันออกงาน WFH"}
                  </Button>
                </div>
              )}

              {!todayQuery.data?.actions.canCheckIn &&
                !todayQuery.data?.actions.canCheckOut && (
                  <div className="text-sm text-gray-600">
                    วันนี้ไม่มี action เพิ่มเติมแล้ว หากต้องการแก้เวลาให้ส่งคำขอด้านล่าง
                  </div>
                )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>ส่งคำขอแก้ไขเวลา</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <select
                value={exceptionType}
                onChange={(e) =>
                  setExceptionType(
                    e.target.value as
                      | "missing_checkout"
                      | "manual_entry"
                      | "time_correction"
                  )
                }
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="manual_entry">ลงเวลาย้อนหลัง</option>
                <option value="missing_checkout">ลืมออกงาน</option>
                <option value="time_correction">แก้ไขเวลา</option>
              </select>
              <input
                type="datetime-local"
                value={requestedCheckInAt}
                onChange={(e) => setRequestedCheckInAt(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
              />
              <input
                type="datetime-local"
                value={requestedCheckOutAt}
                onChange={(e) => setRequestedCheckOutAt(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
              />
              <textarea
                value={exceptionReason}
                onChange={(e) => setExceptionReason(e.target.value)}
                rows={4}
                placeholder="อธิบายสาเหตุที่ต้องขอแก้ไขเวลา"
                className="w-full border rounded-lg px-3 py-2"
              />
              <Button
                onClick={() => exceptionMutation.mutate()}
                disabled={!exceptionReason.trim() || exceptionMutation.isPending}
                className="w-full"
              >
                {exceptionMutation.isPending ? "กำลังส่ง..." : "ส่งคำขอแก้ไขเวลา"}
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>ประวัติ WFH ล่าสุด</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {historyQuery.data?.items.length ? (
                historyQuery.data.items.map((item) => (
                  <div
                    key={item.id}
                    className="border rounded-lg p-4 flex items-start justify-between gap-4"
                  >
                    <div>
                      <div className="font-semibold">{item.workDate}</div>
                      <div className="text-sm text-gray-600">
                        เข้า {formatDateTime(item.checkInAt)} / ออก{" "}
                        {formatDateTime(item.checkOutAt)}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {item.workedMinutes ? `${item.workedMinutes} นาที` : "ยังไม่ครบเวลา"}
                      </div>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${statusClass(
                        item.status
                      )}`}
                    >
                      {statusLabel(item.status)}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500">ยังไม่มีประวัติ WFH</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>คำขอแก้ไขเวลาของฉัน</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {exceptionsQuery.data?.items.length ? (
                exceptionsQuery.data.items.map((item) => (
                  <div key={item.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="font-semibold">{item.type}</div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusClass(item.status === "resolved" ? "completed" : item.status === "rejected" ? undefined : "checked_in")}`}>
                        {item.status}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mt-2">{item.reason}</div>
                    <div className="text-xs text-gray-500 mt-2">
                      ขอแก้เมื่อ {formatDateTime(item.createdAt)}
                    </div>
                    {item.resolutionNote && (
                      <div className="text-xs text-gray-600 mt-2">
                        หมายเหตุผู้อนุมัติ: {item.resolutionNote}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500">ยังไม่มีคำขอแก้ไขเวลา</div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}

export default WfhAttendancePage;
